
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CompanySelection from "./pages/CompanySelection";
import CompanyManagement from "./pages/CompanyManagement";
import Dashboard from "./pages/Dashboard";
import ProductOverview from "./pages/ProductOverview";
import ProductManagement from "./pages/ProductManagement";
import ProductEdit from "./pages/ProductEdit";
import StockManagement from "./pages/StockManagement";
import ProductStatistics from "./pages/ProductStatistics";
import ProductExpirySummary from "./pages/ProductExpirySummary";
import ProductExpiryProcessing from "./pages/ProductExpiryProcessing";
import BestExpiryDateImport from "./pages/BestExpiryDateImport";
import ProductImport from "./pages/ProductImport";
import ProductStockImport from "./pages/ProductStockImport";
import ProductPendingImport from "./pages/ProductPendingImport";
import Product3PLImport from "./pages/Product3PLImport";
import OrderOverview from "./pages/OrderOverview";
import SalesStatistics from "./pages/SalesStatistics";
import SalesAnalysis from "./pages/SalesAnalysis";
import TrafficAnalysis from "./pages/TrafficAnalysis";
import SalesImport from "./pages/SalesImport";
import SalesImportSimplified from "./pages/SalesImportSimplified";
import DocumentOverview from "./pages/DocumentOverview";
import RuleSettings from "./pages/RuleSettings";
import SuggestedOrders from "./pages/SuggestedOrders";
import CrossCompanySuggestions from "./pages/CrossCompanySuggestions";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "@/components/ErrorBoundary";
import SlowMovingProducts from "./pages/SlowMovingProducts";
import PriceAnalysis from "./pages/PriceAnalysis";
import HQPurchasing from "./pages/hq/HQPurchasing";
import HQWholesale from "./pages/hq/HQWholesale";
import HQAccounting from "./pages/hq/HQAccounting";
import HQRouteGuard from "./components/HQRouteGuard";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ErrorBoundary>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/company-selection" element={<CompanySelection />} />
            <Route path="/company-management" element={<CompanyManagement />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/:companyId" element={<Dashboard />} />
            <Route path="/product-overview" element={<ProductOverview />} />
            <Route path="/product-overview/management" element={<ProductManagement />} />
            <Route path="/product-overview/management/edit/:id" element={<ProductEdit />} />
            <Route path="/product-overview/stock" element={<StockManagement />} />
            <Route path="/product-overview/statistics" element={<ProductStatistics />} />
            <Route path="/product-overview/expiry" element={<ProductExpirySummary />} />
            <Route path="/product-overview/expiry-processing" element={<ProductExpiryProcessing />} />
            <Route path="/product-overview/best-expiry-import" element={<BestExpiryDateImport />} />
            <Route path="/product-overview/import" element={<ProductImport />} />
            <Route path="/product-overview/import-stock" element={<ProductStockImport />} />
            <Route path="/product-overview/import-pending" element={<ProductPendingImport />} />
            <Route path="/product-overview/import-3pl" element={<Product3PLImport />} />
            <Route path="/orders/overview" element={<DocumentOverview />} />
            <Route path="/orders/order-overview" element={<OrderOverview />} />
            <Route path="/orders/statistics" element={<SalesStatistics />} />
            <Route path="/analytics/sales-analysis" element={<SalesAnalysis />} />
            <Route path="/orders/import" element={<SalesImport />} />
            <Route path="/orders/import-simplified" element={<SalesImportSimplified />} />
            <Route path="/purchase/rule-settings" element={<RuleSettings />} />
            <Route path="/purchase/suggested-orders" element={<SuggestedOrders />} />
            <Route path="/purchase/cross-company-suggestions" element={<CrossCompanySuggestions />} />
            <Route path="/analytics/slow-moving" element={<SlowMovingProducts />} />
            <Route path="/analytics/traffic-analysis" element={<TrafficAnalysis />} />
            <Route path="/analytics/price-analysis" element={<PriceAnalysis />} />
            <Route path="/hq/purchasing" element={<HQRouteGuard><HQPurchasing /></HQRouteGuard>} />
            <Route path="/hq/wholesale" element={<HQRouteGuard><HQWholesale /></HQRouteGuard>} />
            <Route path="/hq/accounting" element={<HQRouteGuard><HQAccounting /></HQRouteGuard>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;

